import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getUITA, isTrue } from '../../../utils/commonUtils';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import { fetchDataSource } from '../../actions/data-source.actions';
import { updateMultiSelectDropDownValues } from '../../actions/runtime.actions';
import { PREDEFINED_DATA_SOURCES } from '../../constants/form-manager.constants';
import { getEntityType } from '../../selectors/common.selectors';
import { getDataSource } from '../../selectors/form-manager.selectors';
import {
  getDataSourceWatchValues,
  getMultiSelectDropDownValues,
} from '../../selectors/runtime.selectors';
import { getDropDownOptions } from '../../utils/controls/ssDropDown.utils';
import {
  equalityFn,
  getOpitonsForPredefinedDataSource,
  showAsteriskBefore,
} from '../../utils/form-manager.utils';

function SSRuntimeDropDown({
  label,
  value = '',
  actions,
  isEditor,
  isRequired,
  dataSourceKey,
  dataValueField,
  dataTextField,
  dataSourceFilter,
  error,
  inTable,
  isHiddenLabel,
  index,
  formKey,
  entityName,
  fieldName,
  instanceId,
  disabled,
  multiSelect,
}) {
  const dispatch = useDispatch();
  const dataSource = useFormSelector(getDataSource, { dataSourceKey }, equalityFn) || {};
  const watchValues = useFormSelector(
    getDataSourceWatchValues,
    {
      conditions: dataSourceFilter?.Conditions || dataSource?.filter?.Conditions || [],
      dataSource,
      index,
      entityName,
    },
    equalityFn
  );
  const entityType = useFormSelector(getEntityType);
  const isRootEntity = entityName === entityType || entityName === 'V_ENU_NONPERSIS_USER_DATA';
  const supportsMultipleOptions = isTrue(multiSelect) && !isRootEntity;
  const multiSelectValues = useFormSelector(
    getMultiSelectDropDownValues,
    { entityName, fieldName, index, supportsMultipleOptions },
    equalityFn
  );

  const [values, setValues] = useState([]);
  const translateString = useTranslation();

  const isDataSourcePredefined = PREDEFINED_DATA_SOURCES.includes(dataSourceKey);
  const valueStr = _.isNil(value) ? '' : value.toString();
  const options = getDropDownOptions({
    multiSelect: supportsMultipleOptions,
    options: values,
    value: valueStr,
    multiSelectValues,
    dataValueField,
    dataTextField,
  });

  // fetch data from dataSource
  useEffect(() => {
    if (!isNilOrEmpty(dataSource) && !isDataSourcePredefined) {
      dispatch(
        fetchDataSource({
          index,
          dataSource,
          dataSourceFilter,
          dataSourceFilterFromDataSource: dataSource.filter,
          formKey,
          entityName,
          isEditor,
        })
      ).then((vals) => setValues(vals.filter((item) => item[dataTextField])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...watchValues, instanceId]);

  // fetch data from redux store if data source is predefined
  useEffect(() => {
    if (isDataSourcePredefined) {
      const _values = getOpitonsForPredefinedDataSource(dataSource, dataSourceKey);
      setValues(_values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = ({ target: { value } }) => {
    if (supportsMultipleOptions) {
      dispatch(
        updateMultiSelectDropDownValues({
          entityName,
          fieldName,
          formKey,
          index,
          selectedValues: value,
        })
      );
    } else {
      actions.updateControlValue(value);
    }
  };

  return (
    <Select
      id={instanceId}
      label={inTable || isHiddenLabel ? '' : label}
      asteriskPosition={showAsteriskBefore(inTable ? '' : label, inTable) ? 'before' : 'after'}
      value={supportsMultipleOptions ? multiSelectValues : valueStr}
      onChange={onChange}
      required={isRequired}
      placeholder={`${translateString('Select Item')}...`}
      error={error}
      style={{
        maxWidth: !inTable ? '100%' : undefined,
        width: '100%',
        minWidth: inTable ? 184 : undefined,
        outline: 'none',
      }}
      ui-ta={
        supportsMultipleOptions
          ? getUITA('FormCtrl', 'SSDropDown', 'multiselect', entityName, fieldName)
          : getUITA('FormCtrl', 'SSDropDown', entityName, fieldName)
      }
      disabled={disabled}
      multiple={supportsMultipleOptions}
    >
      {options.map((item, i) =>
        isDataSourcePredefined ? (
          <MenuItem
            id={getUITA('FormCtrl', 'SSDropDownItem', entityName, fieldName)}
            key={item.id}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        ) : (
          <MenuItem
            id={getUITA('FormCtrl', 'SSDropDownItem', entityName, fieldName)}
            key={`${item[dataTextField]}_${item[dataValueField]}_${i}`}
            value={item[dataValueField]}
          >
            {item.LNG_TRANSLATION || item[dataTextField]}
          </MenuItem>
        )
      )}
    </Select>
  );
}

export default SSRuntimeDropDown;
